import React, { useContext, memo, Suspense } from 'react';
import { Grid } from '@material-ui/core';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { SectionSkeleton } from '../common/Loaders/Loaders';
import { isB2BUser, isB2Badmin, defineUserType } from '../../utils/utils';
import {
  UNVERIFIED_DIY,
  UNVERIFIED_ADMIN,
  UNVERIFIED_PURCHASER,
  VERIFIED_USER,
  VERIFIED_ADMIN,
  VERIFIED_PURCHASER,
} from '../../constants/constants';
import type { MilitaryDiscountDetails } from '../../api/types';
import type { ExperienceContextValue } from '../../types/experience-context';

export interface ProgramSectionProps {
  apiResponse?: MilitaryDiscountDetails | null;
  isEnrolled?: boolean;
  verifyOnClick?: (isSpouse: boolean) => void;
  customerAccountId?: string;
  userId?: string;
}

const UnverifiedDIY = React.lazy(
  () =>
    import(
      /* webpackChunkName: "UnverifiedDIY" */
      './MilitaryDiscountText/UnverifiedDIY'
    ),
);

const UnverifiedAdmin = React.lazy(
  () =>
    import(
      /* webpackChunkName: "UnverifiedAdmin" */
      './MilitaryDiscountText/UnverifiedAdmin'
    ),
);

const UnverifiedPurchaser = React.lazy(
  () =>
    import(
      /* webpackChunkName: "UnverifiedPurchaser" */
      './MilitaryDiscountText/UnverifiedPurchaser'
    ),
);

const VerifiedUser = React.lazy(
  () =>
    import(
      /* webpackChunkName: "VerifiedUser" */
      './MilitaryDiscountText/VerifiedUser'
    ),
);

function ProgramSection(props: ProgramSectionProps) {
  const { channel } = useContext<ExperienceContextValue>(ExperienceContext);
  const isMobile = channel === 'mobile';
  /* Handed down rather than read off `apiResponse` here: a customer who has just
  /* verified counts as enrolled before the enrollment API says so, and the page
  /* is where that is reconciled. See the derivation in pages/LandingPage.tsx. */
  const {
    apiResponse = null,
    isEnrolled = false,
    verifyOnClick = () => {},
    customerAccountId,
    userId,
  } = props;

  /* These values to be used after SVOC updates for verified Admins and Purchasers.
  /* `name` is a nested object on `enrolledProfile` now that the enrollment
  /* details come from `militaryDiscountDetails` - it maps V2Name, where the REST
  /* payload had the two fields flat on the profile. */
  // const businessName = enrolledProfile?.companyName;
  // const firstName = enrolledProfile?.name?.firstName;
  // const lastName = enrolledProfile?.name?.lastName;
  const isMilitaryDiscountCapped = apiResponse?.isMilitaryDiscountCapped;
  const isB2B = isB2BUser();
  const isAdmin = isB2Badmin();
  const userType = defineUserType(isB2B, isAdmin, isEnrolled);
  const isUserVerified =
    userType === VERIFIED_USER ||
    userType === VERIFIED_ADMIN ||
    userType === VERIFIED_PURCHASER;

  return (
    <Grid container xs={12}>
      <Suspense fallback={<SectionSkeleton section="program" />}>
        {userType === UNVERIFIED_DIY && (
          <UnverifiedDIY
            channelId={isMobile ? 2 : 1}
            customerAccountId={customerAccountId}
            userId={userId}
            verifyOnClick={verifyOnClick}
          />
        )}
        {userType === UNVERIFIED_ADMIN && (
          <UnverifiedAdmin
            channelId={isMobile ? 2 : 1}
            customerAccountId={customerAccountId}
            userId={userId}
            verifyOnClick={verifyOnClick}
          />
        )}
        {userType === UNVERIFIED_PURCHASER && <UnverifiedPurchaser />}
        {isUserVerified && (
          <VerifiedUser
            isMilitaryDiscountCapped={isMilitaryDiscountCapped}
            verificationDate={apiResponse?.enrolledProfile?.verificationDate}
            userType={userType}
          />
        )}
      </Suspense>
    </Grid>
  );
}

export default memo(ProgramSection);
